import { useCallback, useEffect, useMemo, useState } from "react";

// The library ID is a public identifier (it was previously exported from
// utility.js). Authentication keys remain server-side.
export const BUNNY_VIDEO_LIBRARY_ID = "16404";
export const BUNNY_STREAM_CDN_BASE =
    "https://vz-db0e7359-d70.b-cdn.net";

const DEFAULT_POLL_INTERVAL = 10000;
const LONG_PROCESSING_ATTEMPTS = 12;

export function getBunnyVideoId(file) {
    return file?.original || file?.key || "";
}

export function getBunnyPlaylistUrl(file) {
    const videoId = getBunnyVideoId(file);
    return videoId
        ? `${BUNNY_STREAM_CDN_BASE}/${videoId}/playlist.m3u8`
        : "";
}

export function getBunnyThumbnailUrl(file, cacheKey = "") {
    const videoId = getBunnyVideoId(file);

    if (!videoId) {
        return "";
    }

    const query = cacheKey ? `?ready=${encodeURIComponent(cacheKey)}` : "";
    return `${BUNNY_STREAM_CDN_BASE}/${videoId}/thumbnail.jpg${query}`;
}

function encodeProgressFromPlayData(playData) {
    const progress = Number(playData?.video?.encodeProgress);

    if (!Number.isFinite(progress) || progress < 0 || progress > 100) {
        return null;
    }

    return Math.round(progress);
}

export function readinessFromPlayData(playData) {
    // Nodiant's player uses the HLS playlist, so an original-only early-play
    // source is not considered ready yet.
    if (
        playData?.isPlaylistPlayable ||
        (playData?.isPlayable &&
            playData?.preferredPlaybackSource === "Playlist")
    ) {
        return {
            status: "ready",
            progress: 100,
            source: "play-data",
        };
    }

    return {
        status: "processing",
        progress: encodeProgressFromPlayData(playData),
        source: "play-data",
    };
}

async function probePlaylist(playlistUrl, signal) {
    if (!playlistUrl) {
        return false;
    }

    const separator = playlistUrl.includes("?") ? "&" : "?";
    const response = await fetch(
        `${playlistUrl}${separator}readiness=${Date.now()}`,
        {
            method: "GET",
            cache: "no-store",
            signal,
        },
    );

    return response.ok;
}

export default function useBunnyVideoReadiness(
    file,
    { enabled = true, pollInterval = DEFAULT_POLL_INTERVAL } = {},
) {
    const videoId = getBunnyVideoId(file);
    const playlistUrl = getBunnyPlaylistUrl(file);
    const [refreshRequest, setRefreshRequest] = useState(0);
    const [readiness, setReadiness] = useState({
        status: enabled && videoId ? "checking" : "idle",
        progress: null,
        attempts: 0,
        source: null,
    });

    const retry = useCallback(() => {
        setRefreshRequest((request) => request + 1);
    }, []);

    useEffect(() => {
        if (!enabled || !videoId) {
            setReadiness({
                status: "idle",
                progress: null,
                attempts: 0,
                source: null,
            });
            return undefined;
        }

        let cancelled = false;
        let timer;
        let attempts = 0;
        const controller = new AbortController();

        setReadiness({
            status: "checking",
            progress: null,
            attempts: 0,
            source: null,
        });

        async function checkReadiness() {
            attempts += 1;

            try {
                const playDataResponse = await fetch(
                    `https://video.bunnycdn.com/library/${BUNNY_VIDEO_LIBRARY_ID}/videos/${videoId}/play`,
                    {
                        cache: "no-store",
                        signal: controller.signal,
                    },
                );

                if (playDataResponse.ok) {
                    const playData = await playDataResponse.json();
                    const nextReadiness = readinessFromPlayData(playData);

                    if (!cancelled) {
                        setReadiness({
                            ...nextReadiness,
                            attempts,
                        });
                    }

                    if (nextReadiness.status === "ready") {
                        return;
                    }
                } else if (playDataResponse.status === 404) {
                    if (!cancelled) {
                        setReadiness({
                            status: "processing",
                            progress: null,
                            attempts,
                            source: "play-data",
                        });
                    }
                } else {
                    const playlistIsReady = await probePlaylist(
                        playlistUrl,
                        controller.signal,
                    );

                    if (!cancelled) {
                        setReadiness({
                            status: playlistIsReady ? "ready" : "unknown",
                            progress: playlistIsReady ? 100 : null,
                            attempts,
                            source: "playlist",
                        });
                    }

                    if (playlistIsReady) {
                        return;
                    }
                }
            } catch (error) {
                if (error.name === "AbortError") {
                    return;
                }

                try {
                    const playlistIsReady = await probePlaylist(
                        playlistUrl,
                        controller.signal,
                    );

                    if (!cancelled) {
                        setReadiness({
                            status: playlistIsReady ? "ready" : "unknown",
                            progress: playlistIsReady ? 100 : null,
                            attempts,
                            source: "playlist",
                        });
                    }

                    if (playlistIsReady) {
                        return;
                    }
                } catch (playlistError) {
                    if (
                        playlistError.name !== "AbortError" &&
                        !cancelled
                    ) {
                        setReadiness({
                            status: "unknown",
                            progress: null,
                            attempts,
                            source: null,
                        });
                    }
                }
            }

            if (!cancelled) {
                timer = window.setTimeout(checkReadiness, pollInterval);
            }
        }

        checkReadiness();

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [enabled, playlistUrl, pollInterval, refreshRequest, videoId]);

    return useMemo(
        () => ({
            ...readiness,
            isReady: readiness.status === "ready",
            isChecking: readiness.status === "checking",
            isProcessing: readiness.status === "processing",
            isTakingLonger:
                readiness.attempts >= LONG_PROCESSING_ATTEMPTS,
            retry,
        }),
        [readiness, retry],
    );
}
